import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type { ProgramSubject } from '@lms/shared/types';

export const subjectKeys = {
  all: ['program-subjects'] as const,
  lists: () => [...subjectKeys.all, 'list'] as const,
  list: (stageId: string) => [...subjectKeys.all, 'list', stageId] as const,
  details: () => [...subjectKeys.all, 'detail'] as const,
  detail: (id: string) => [...subjectKeys.details(), id] as const,
};

export function useSubjects(stageId: string) {
  return useQuery({
    queryKey: subjectKeys.list(stageId),
    queryFn: () => apiClient.programSubjects.list(stageId),
    enabled: !!stageId,
  });
}

export function useCreateSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      data: Partial<ProgramSubject> & { stageId: string; name: string };
    }) => apiClient.programSubjects.create(variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
    },
  });
}

export function useUpdateSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string; data: Partial<ProgramSubject> }) =>
      apiClient.programSubjects.update(variables.id, variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
    },
  });
}

export function useDeleteSubject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string }) =>
      apiClient.programSubjects.delete(variables.id),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: subjectKeys.lists() });
    },
  });
}
